import * as React from 'react';

import type { BlockUpdatesData, BlockUpdatesDateFormat } from '@gitbook/doc-core';
import { updates } from '@gitbook/doc-core';
import type { BlockProps } from '@gitbook/slate-react';
import { InsertLineButton } from '@gitbook/spine-react';

import { BlockWrapper, useBlockPaletteFactory } from '../common';

export interface UpdatesFormatContextValue {
    format: BlockUpdatesDateFormat;
    updateFormat: (format: BlockUpdatesDateFormat) => void;
}

export const UpdatesFormatContext = React.createContext<UpdatesFormatContextValue | null>(null);

export function BlockUpdates(props: BlockProps) {
    const { node, editor } = props;
    const palette = useBlockPaletteFactory({ nodeKey: node.key });

    // Read format from the Updates block's data
    const updatesData = node.data.toFrozenObject<BlockUpdatesData>();
    const format = updatesData.format;

    const updateFormat = React.useCallback(
        (nextFormat: BlockUpdatesDateFormat) => {
            if (nextFormat === format) {
                return;
            }

            editor.change((change) => {
                change.setNodeByKey(node.key, {
                    data: node.data.set('format', nextFormat),
                });
            });
        },
        [editor, node.key, node.data, format]
    );

    const formatContextValue = React.useMemo(
        () => ({ format, updateFormat }),
        [format, updateFormat]
    );

    const handleAddUpdate = ({ position }: { position: 'before' | 'after' }) => {
        editor.change((change) => {
            updates.insertUpdateAt(change, node.key, position === 'before' ? 0 : node.nodes.size);
        });
    };

    return (
        <UpdatesFormatContext.Provider value={formatContextValue}>
            <BlockWrapper {...props} palette={palette} contentProps={{ className: 'relative' }}>
                {!editor.readOnly ? (
                    <InsertLineButton
                        tooltip="Insert update"
                        className="-top-5 absolute right-0 left-0"
                        onPress={() => handleAddUpdate({ position: 'before' })}
                    />
                ) : null}

                {props.children}

                {!editor.readOnly ? (
                    <InsertLineButton
                        tooltip="Insert update"
                        onPress={() => handleAddUpdate({ position: 'after' })}
                        className="-bottom-5 absolute right-0 left-0"
                    />
                ) : null}
            </BlockWrapper>
        </UpdatesFormatContext.Provider>
    );
}
